/**
 * Choosing a 汉字's parts on a 复习 card, a part at a time.
 *
 * The card draws an empty sum, a slot per part joined by plus signs, and a row
 * of tiles under it, the parts and two decoys shuffled together. A tap on a
 * tile puts its part in the next empty slot, `女 + 子`, and leaves the tile's
 * place in the row empty. A tap on a part in the sum, Undo, or Backspace takes
 * a part back out. After each move the hidden field holds the tiles' places,
 * in slot order, which is what Check posts. See
 * `src/user/fuxi-assembly-markup.ts`.
 *
 * **Nothing is put together yet.** The parts stay characters in a sum until the
 * card is checked, and the marked card is where they go into the square
 * (`./fuxi-assembled.ts`). A part drawn into its place as it was chosen would
 * look right or wrong before the reader had committed to it.
 *
 * **The last slot checks the card.** Filling it is the reader's answer, and a
 * press on Check after it would be one more thing between them and the next
 * card. The press waits long enough for the part to land, and a move made in
 * that time stops it.
 *
 * **A part flies from its tile to its slot**, as a copy of the tile's
 * characters drawn over the page. A reader who asked for less motion gets the
 * part in the slot at once.
 *
 * **Delegated on the document**, the way `./fuxi-tiles.ts` listens, since the
 * card is swapped in and out all through a session.
 */

import { flyText, wantsStillness } from "./fuxi-assembly-flight.js";

/**
 * The event a full sum sends as it checks itself.
 *
 * A 复习 card checks by submitting its form, and a 拼字 Exercise on its page
 * has no form, so it hears this instead (`./lianxi-pinzi.ts`).
 */
export const assemblyFull = "cb-fuxi:assembly-full";

/** The card's sum, tiles and field together. */
const card = "[data-cb-fuxi-zuzi]";

/** A tile in the row, which names its place. */
const tile = "[data-cb-fuxi-part]";

/** A slot in the sum. */
const slot = "[data-cb-fuxi-zuzi-sum] [data-cb-fuxi-slot]";

/** A filled slot, which names the tile its part came from. */
const filled = `${slot}[data-cb-fuxi-from]`;

/** How long a part takes to fly into its slot. */
const flight = 240;

/** How long the card waits after the last slot before it checks itself. */
const settle = 380;

/** The checks waiting to be pressed, one per card. */
const waiting = new WeakMap<Element, number>();

/** The slots, in the order the parts fill them. */
function slots(at: Element): readonly HTMLElement[] {
  return [...at.querySelectorAll<HTMLElement>(slot)];
}

/** Whether a slot holds a part. */
function holds(one: Element): boolean {
  return one.hasAttribute("data-cb-fuxi-from");
}

/** Write the order into the field and say what the sum holds. */
function record(at: Element): void {
  const placed = slots(at).filter(holds);
  // `fuxiAssembly` draws the field, the spoken sum and Undo in every card.
  const field = at.querySelector<HTMLInputElement>("[data-cb-fuxi-arranged]")!;
  const said = at.querySelector("[data-cb-fuxi-zuzi-said]")!;
  const undo = at.querySelector<HTMLButtonElement>("[data-cb-fuxi-zuzi-undo]")!;
  const next = slots(at).find((one) => !holds(one));

  field.value = placed
    .map((one) => one.getAttribute("data-cb-fuxi-from"))
    .join(" ");

  said.textContent = placed.map((one) => one.textContent).join(" + ");
  undo.disabled = placed.length === 0;

  for (const one of slots(at)) {
    one.classList.toggle("cb-fuxi-zuzi-next", one === next);
  }
}

/** Press Check once the last part has landed, unless a move comes first. */
function checkWhenFull(at: Element): void {
  const page = at.ownerDocument;
  const view = page.defaultView;
  const previous = waiting.get(at);

  if (previous !== undefined) {
    view?.clearTimeout(previous);
    waiting.delete(at);
  }

  const full = slots(at).every(holds);

  at.classList.toggle("cb-fuxi-zuzi-full", full);

  if (!full || view === null) {
    return;
  }

  waiting.set(
    at,
    view.setTimeout(
      () => {
        waiting.delete(at);

        const form = at.closest("form");

        if (!at.isConnected) {
          return;
        }

        at.dispatchEvent(new CustomEvent(assemblyFull, { bubbles: true }));

        if (form?.isConnected === true) {
          form.requestSubmit(form.querySelector(".cb-fuxi-check"));
        }
      },
      wantsStillness(page) ? 0 : settle,
    ),
  );
}

/**
 * Put a tile's part in the next empty slot.
 *
 * @param at - The card.
 * @param chosen - The tile in the row.
 */
export function placePart(at: Element, chosen: HTMLElement): void {
  const into = slots(at).find((one) => !holds(one));

  if (into === undefined) {
    return;
  }

  const shown = chosen.querySelector("[lang]")!;

  into.textContent = shown.textContent;
  into.setAttribute(
    "data-cb-fuxi-from",
    chosen.getAttribute("data-cb-fuxi-part")!,
  );
  into.classList.add("cb-fuxi-zuzi-landing");
  void flyText({ from: shown, to: into, duration: flight }).then(() => {
    into.classList.remove("cb-fuxi-zuzi-landing");
  });
  chosen.setAttribute("disabled", "");
  record(at);
  checkWhenFull(at);
}

/**
 * Take a part out of the sum and give its tile back.
 *
 * @param at - The card.
 * @param placed - The filled slot.
 */
export function takePartBack(at: Element, placed: Element): void {
  // Both callers pass a slot found by the attribute naming its tile.
  const from = placed.getAttribute("data-cb-fuxi-from")!;
  const chosen = at.querySelector<HTMLElement>(`[data-cb-fuxi-part="${from}"]`);

  placed.removeAttribute("data-cb-fuxi-from");
  placed.textContent = "";
  chosen?.removeAttribute("disabled");
  record(at);
  checkWhenFull(at);
}

/** The part placed last, which Undo and Backspace take back. */
function lastPlaced(at: Element): Element | undefined {
  return [...at.querySelectorAll(filled)].at(-1);
}

/** Move whichever tile or part was pressed. */
function startPressing(page: Document): void {
  page.addEventListener("click", (event: Event) => {
    const pressed = event.target as Element;
    const at = pressed.closest(card);

    if (at === null) {
      return;
    }

    const chosen = pressed.closest<HTMLElement>(`${tile}:not([disabled])`);
    const placed = pressed.closest(filled);
    const undo = pressed.closest("[data-cb-fuxi-zuzi-undo]");

    if (chosen !== null) {
      placePart(at, chosen);
    } else if (placed !== null) {
      takePartBack(at, placed);
    } else if (undo !== null) {
      // Undo is disabled while the sum is empty, and a disabled button
      // sends no click, so a part has been placed.
      takePartBack(at, lastPlaced(at)!);
    }
  });
}

/** Whether the key was meant for something the reader is typing into. */
function isTyping(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

/** Place a tile by its digit, or take the last part back. */
function startKeys(page: Document): void {
  page.addEventListener("keydown", (event: KeyboardEvent) => {
    const at = page.querySelector(card);

    if (
      at === null ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      isTyping(event.target)
    ) {
      return;
    }

    if (event.key === "Backspace") {
      const last = lastPlaced(at);

      if (last !== undefined) {
        event.preventDefault();
        takePartBack(at, last);
      }

      return;
    }

    const digit = /^[1-9]$/u.test(event.key) ? Number(event.key) - 1 : -1;
    const chosen = at.querySelector<HTMLElement>(
      `[data-cb-fuxi-part="${String(digit)}"]:not([disabled])`,
    );

    if (chosen !== null) {
      event.preventDefault();
      placePart(at, chosen);
    }
  });
}

/**
 * Choose the parts on every card, by pointer or by key.
 *
 * @param page - The page the bundle is running on.
 */
export function startFuxiAssembly(page: Document): void {
  startPressing(page);
  startKeys(page);
}
